<template>
  <!--
    Landing page (/lp) — standalone marketing page for אקדמיית TOV.
    Header/footer are disabled in the route so this page owns its own top bar.
    All CTAs funnel to /signup, whose router guard opens the signup modal.
    The category collage is a single baked image at public/lp-assets/collage.png.
  -->
  <div class="lp" dir="rtl">

    <!-- ─── Top bar ─── -->
    <header class="lp-topbar">
      <div class="lp-topbar__brand">
        <span class="lp-topbar__logo" aria-hidden="true"></span>
        <span class="lp-topbar__divider" aria-hidden="true"></span>
        <span class="lp-topbar__titles">
          <strong class="lp-topbar__name">אקדמיית TOV</strong>
          <small class="lp-topbar__sub">פלטפורמת הסטרימינג של ערוץ TOV</small>
        </span>
      </div>
    </header>

    <!-- ─── Hero ─── -->
    <section class="lp-hero">
      <!-- Category collage — single baked image (labels + tilt + dark-left fade).
           Drop the art at app/public/lp-assets/collage.png -->
      <img src="../assets/collage.png" alt="" class="lp-hero__collage" aria-hidden="true" />

      <div class="lp-hero__inner">


        <div class="lp-hero__copy">
          <h1 class="lp-hero__title">
            הידע שלא תמצאו<br />
            <span class="lp-hero__title-accent">בשום מקום אחר</span>
          </h1>
          <span class="lp-hero__rule" aria-hidden="true"></span>

          <p class="lp-hero__lead">
            <span class="lp-hero__hl">בלעדי למנויי הסטרימינג</span> — תוכן ייחודי של ערוץ
            TOV: הרצאות, פודקאסטים ותכני עומק שלא זמינים בשום מקום אחר.
          </p>

          <p class="lp-hero__body">
            בנוסף — כל תכני ערוץ TOV
            <span class="lp-hero__hl">ללא פרסומות ולפני כולם</span>,
            לצפייה מוקדמת ובלעדית למנויי הסטרימינג.
          </p>

          <p class="lp-hero__cats">
            היסטוריה · משפט · ביטחון · יהדות · העולם הערבי · פוליטיקה ·
            מלחמות · תרבות · מדע · גיאופוליטיקה — ועוד ועוד.
          </p>

          <div class="lp-hero__actions">
            <button type="button" class="lp-btn lp-btn--primary" @click="goSignup">
              הצטרפו עכשיו
              <span class="lp-btn__ico" v-html="icon.chev" aria-hidden="true"></span>
            </button>
            <button type="button" class="lp-btn lp-btn--outline" @click="goWatch">
              לצפייה בתכנים
              <span class="lp-btn__ico" v-html="icon.playTri" aria-hidden="true"></span>
            </button>
          </div>
        </div>

      </div>

      <!-- ─── Feature strip ─── -->
      <ul class="lp-features">
        <li class="lp-feature" v-for="f in features" :key="f.title">
          <span class="lp-feature__icon" v-html="f.icon"></span>
          <span class="lp-feature__text">{{ f.title }}</span>
        </li>
      </ul>
    </section>

    <!-- ══════ SECTION 1 — Why join ══════ -->
    <section class="lp-sec lp-why">
      <div class="lp-sec__inner">
        <div class="lp-why__grid">
          <div class="lp-why__copy">
            <p class="lp-eyebrow lp-eyebrow--start"><span class="lp-eyebrow__dash"></span>למה להצטרף?</p>
            <h2 class="lp-sec__title">
              ספריית ידע <span class="lp-red">ישראלית אמיתית</span>
            </h2>
            <span class="lp-sec__rule" aria-hidden="true"></span>

            <div class="lp-why__body">
              <p>
                אקדמיית TOV נבנתה כדי ליצור אלטרנטיבה להשתלטות הפרוגרסיבים על
                האקדמיות במערב. כדי לספר את הסיפור האמיתי ולהילחם בנרטיבים שקריים,
                יצאנו יחד עם טובי המומחים בישראל למסע אינטלקטואלי מעמיק ומרתק לתיקון —
                עבור אלה שרוצים להבין את הסיפורים שמעצבים את ישראל, המזרח התיכון והעולם.
              </p>
              <p>
                במקום רעש, תקבלו תוכן אמיתי. במקום קליפים תזזיתיים, תקבלו הרצאות עומק
                מאלה שחקרו, פיקדו, כתבו ולימדו במשך עשרות שנים.
              </p>
            </div>
          </div>

          <!-- Speaker marquee — 3 auto-scrolling rows (modern CSS shape()/sibling-*()
               marquee; static-row fallback on browsers without offset-path). -->
          <div class="lp-marquee" aria-hidden="true">
            <div
              v-for="(row, ri) in starRows"
              :key="ri"
              class="lp-marquee__row"
              :class="{ 'lp-marquee__row--rev': ri % 2 === 1 }"
            >
              <div
                v-for="(s, k) in row"
                :key="k"
                class="lp-star"
                :class="{ 'lp-star--empty': !s.image }"
                :style="s.image ? { backgroundImage: 'url(' + s.image + ')' } : null"
              >
                <span class="lp-star__name">{{ s.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <ul class="lp-features lp-features--4">
          <li class="lp-feature" v-for="f in whyFeatures" :key="f.title">
            <span class="lp-feature__icon" v-html="f.icon"></span>
            <span class="lp-feature__text">{{ f.title }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ══════ SECTION 2 — What's inside ══════ -->
    <section class="lp-sec lp-inside">
      <div class="lp-sec__inner">
        <p class="lp-eyebrow"><span class="lp-eyebrow__dash"></span>מה מחכה לכם בפנים?<span class="lp-eyebrow__dash"></span></p>
        <h2 class="lp-sec__title lp-sec__title--center">
          תוכן עמוק. נקי מפרסומות. <span class="lp-red">נבנה בשבילכם.</span>
        </h2>

        <div class="lp-cards">
          <article class="lp-card" v-for="c in insideCards" :key="c.title">
            <div class="lp-card__media" :style="{ backgroundImage: 'url(' + c.img + ')' }">
              <span class="lp-card__icon" v-html="c.icon"></span>
            </div>
            <h3 class="lp-card__title">{{ c.title }}</h3>
            <p class="lp-card__body">{{ c.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- ══════ SECTION 3 — Who it's for ══════ -->
    <section class="lp-sec lp-audience">
      <div class="lp-sec__inner lp-audience__inner">
        <p class="lp-eyebrow"><span class="lp-eyebrow__dash"></span>למי זה מיועד?<span class="lp-eyebrow__dash"></span></p>
      <div class="lp-sec__inner lp-manifesto__inner">
        <p class="lp-manifesto__lead">מי שרוצה להבין באמת — לאנשים שחושבים לעומק</p>
        <h2 class="lp-manifesto__title">
          בעולם של מסרים מסולפים ואג'נדות ריקות,<br />
          אנחנו בונים בית ל<span class="lp-red">תוכן של אמת</span>
        </h2>
        <p class="lp-manifesto__sub">
          בלי פילטרים וללא התנצלות. תוכן אינטלקטואלי מרגש מתוך
          <span class="lp-red">זהות יהודית וציונית</span>.
        </p>
        <p class="lp-manifesto__cta">
          הצטרפו כמנויים וקחו חלק בפלטפורמת העתיד של מדינת ישראל.
        </p>
      </div>
   
      </div>
      <!-- Angled photo strip: app/public/lp-assets/audience-strip.jpg -->
      <img class="lp-audience__strip" aria-hidden="true" src="../assets/strip-collage.png" />
    </section>
    <!-- ─── Sticky CTA (follows on scroll) ─── -->
    <transition name="lp-sticky">
      <div class="lp-sticky" v-show="showSticky">
        <span class="lp-sticky__text">גישה בלעדית לכל תכני TOV — ללא פרסומות ולפני כולם</span>
        <button type="button" class="lp-btn lp-btn--primary lp-btn--sm" @click="goSignup">
          הצטרפו עכשיו
        </button>
      </div>
    </transition>

  </div>
</template>

<script>
// Inline stroke icons (currentColor → accent red). Shared across feature strips.
const S = (p) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
const ICON = {
  calendar: S('<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/><rect x="7" y="12" width="2.5" height="2.5"/><rect x="14.5" y="12" width="2.5" height="2.5"/>'),
  devices: S('<rect x="2" y="4" width="14" height="10" rx="1.5"/><line x1="6" y1="18" x2="12" y2="18"/><rect x="17" y="8" width="5" height="12" rx="1.2"/>'),
  crown: S('<path d="M3 8l3.5 3L12 5l5.5 6L21 8l-1.5 10h-15L3 8z"/><line x1="4.5" y1="18" x2="19.5" y2="18"/>'),
  clock: S('<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><polyline points="3 3 3 8 8 8"/><polyline points="12 8 12 12 15 14"/>'),
  lock: S('<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>'),
  people: S('<circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><circle cx="17" cy="9" r="2.2"/><path d="M15 20a5 5 0 0 1 6.5-4.8"/>'),
  shield: S('<path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z"/><path d="M9 12l2 2 4-4"/>'),
  cap: S('<path d="M2 8l10-4 10 4-10 4z"/><path d="M6 10v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5"/><line x1="22" y1="8" x2="22" y2="13"/>'),
  mic: S('<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0"/><line x1="12" y1="17" x2="12" y2="21"/><line x1="8" y1="21" x2="16" y2="21"/>'),
  play: S('<circle cx="12" cy="12" r="9"/><path d="M10 8l6 4-6 4z" fill="currentColor" stroke="none"/>'),
  book: S('<path d="M12 6c-1.8-1.2-4-1.5-6-1.5-1 0-2 .2-2 .2v13s1-.2 2-.2c2 0 4.2.3 6 1.5 1.8-1.2 4-1.5 6-1.5 1 0 2 .2 2 .2v-13s-1-.2-2-.2c-2 0-4.2.3-6 1.5z"/><line x1="12" y1="6" x2="12" y2="19"/>'),
  // RTL-forward glyphs — SVG so the bidi algorithm can't mirror them.
  chev: S('<polyline points="15 6 9 12 15 18"/>'),
  playTri: S('<path d="M15 8l-6 4 6 4z" fill="currentColor" stroke="none"/>'),
};

export default {
  name: 'LandingPage',
  data() {
    return {
      showSticky: false,
      icon: ICON,

      // Admin-managed marquee people [{name, image}] — fetched on mount (see loadSpeakers).
      speakers: [],

      // Fallback names if the API is empty/unreachable (no photos → gradient placeholder).
      stars: [
        'עוזי דיין', 'מוטי קידר', 'גרשון הכהן', 'טל אורנן', 'פיני יחזקאלי',
        'צבי סדן', 'אביגדור קהלני', 'אלדד בק', 'טופז רם', 'אדי כהן',
        'דוד אידר', 'מוטי קוברסקי', 'הרב זיני', 'הרב שרקי', 'אלכס גרינברג',
        'נגה ארבל', 'גלי בת חורין', 'סמדר', 'הילה שמואלי', 'אורן סולמון',
        'אודליה קדמי', 'אבי דיסקין', 'אליהו יוסיאן', 'דוד פסיג', 'אלעזר שטורם',
        'הרב זאגא', 'חנן עמיאור', 'מור אלטשולר', 'אורי מילשטיין', 'רונן כהן',
        'יורם שפטל', 'מיכאל דבורין', 'אלישע האס', 'עמיהוד אמיר', 'מיכאל בן ארי',
        'מרדי בן חמו',
      ],

      whyFeatures: [
        { title: 'תוכן בלעדי מעמיק ומעשיר', icon: ICON.lock },
        { title: 'מרצים מהמובילים בישראל', icon: ICON.people },
        { title: 'מגוון רחב של תחומים', icon: ICON.shield },
        { title: 'זווית בלעדית וייחודית', icon: ICON.cap },
      ],

      insideCards: [
        {
          title: 'הרצאות פרימיום בלעדיות',
          body: 'המרצים והחוקרים הבולטים ביותר בישראל במקום אחד. סדרות עומק על היסטוריה, ביטחון, יהדות, משפט, מודיעין, פוליטיקה וגיאופוליטיקה.',
          img: require('../assets/inside-1.png'), icon: ICON.cap,
        },
        {
          title: 'הפודקאסטים של TOV — בלי פרסומות',
          body: 'כל הראיונות והתוכניות שאתם אוהבים. נקיים מפרסומות, מסודרים לפי נושאים ובאיכות צפייה גבוהה.',
          img: require('../assets/inside-2.png'), icon: ICON.mic,
        },
        {
          title: 'צפייה מוקדמת לפני כולם',
          body: 'הפרקים עולים למנויים לפני הפרסום ביוטיוב. גישה מוקדמת לתוכן החדש של הערוץ.',
          img: require('../assets/inside-3.png'), icon: ICON.play,
        },
        {
          title: 'קורסים וסדרות עומק',
          body: 'קורסים בנושאי ביטחון, תקשורת, היסטוריה, המזרח התיכון, אסטרטגיה ומחשבה יהודית. תוכן שנבנה במיוחד ללמידה מסודרת.',
          img: require('../assets/inside-4.png'), icon: ICON.book,
        },
      ],

      ctaFeatures: [
        { title: 'תוכן חדש מדי שבוע', icon: ICON.calendar },
        { title: 'צפייה מכל מקום', icon: ICON.devices },
        { title: 'ביטול בכל עת', icon: ICON.clock },
      ],

      features: [
        { title: 'תוכן חדש מדי שבוע', icon: ICON.calendar },
        { title: 'צפייה מכל מכשיר', icon: ICON.devices },
        { title: 'ללא פרסומות ולפני כולם', icon: ICON.crown },
      ],
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
    this.loadSpeakers();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  computed: {
    // Round-robin into 3 marquee rows. Admin data when present, else name fallback.
    starRows() {
      const src = this.speakers.length
        ? this.speakers
        : this.stars.map((name) => ({ name, image: '' }));
      const rows = [[], [], []];
      src.forEach((s, i) => rows[i % 3].push(s));
      return rows;
    },
  },
  methods: {
    onScroll() {
      this.showSticky = window.scrollY > 320;
    },
    // Load admin-managed marquee speakers (api controller, method lp_speakers).
    loadSpeakers() {
      this.$http.post(window.api_url + 'lp_speakers').then((resp) => {
        const list = resp && resp.data && resp.data.data && resp.data.data.speakers;
        if (Array.isArray(list) && list.length) this.speakers = list;
      }).catch(() => { /* keep fallback names */ });
    },
    goSignup() {
      this.$router.push('/signup');
    },
    goWatch() {
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.lp {
  min-height: 100vh;
  background:
    radial-gradient(120% 90% at 85% 0%, #16233f 0%, transparent 55%),
    var(--color-bg, #000);
  color: var(--color-text, #f5f5f5);
  font-family: var(--font-hebrew, 'Heebo', sans-serif);
  overflow-x: hidden;
}

/* ── Top bar ── */
.lp-topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  padding: 16px clamp(20px, 5vw, 64px);
  background: rgba(6, 8, 14, 0.82);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.lp-topbar__brand { display: flex; align-items: center; gap: 16px; }
.lp-topbar__logo {
  width: 74px; height: 40px;
  background: url(../assets/logo.png) no-repeat center / contain;
}
.lp-topbar__divider { width: 1px; height: 34px; background: rgba(255, 255, 255, 0.16); }
.lp-topbar__titles { display: flex; flex-direction: column; line-height: 1.2; }
.lp-topbar__name { font-size: 20px; font-weight: 700; }
.lp-topbar__sub { font-size: 12px; color: var(--color-textMuted, #a8a8a8); }

/* ── Hero ── */
.lp-hero {
  position: relative;
  padding: 36px 64px 0;
  overflow: hidden;
}
.lp-hero__collage {
  position: absolute;
  bottom: 25%;
  right: 0;
  width: 50vw;
  height: auto;
  pointer-events: none;
  z-index: 0;
}
.lp-hero__inner {
  position: relative;
  z-index: 1;
  direction: ltr; /* flex-start = left; text re-set to rtl on the copy block */
  max-width: 1440px;
  margin: 0 auto;
  min-height: clamp(420px, 46vw, 620px);
  display: flex;
  align-items: center;
}
.lp-hero__copy { direction: rtl; max-width: 48%; }
.lp-hero__title {
  font-size: clamp(40px, 6vw, 72px);
  font-weight: 800;
  line-height: 1.04;
  letter-spacing: -1px;
  margin: 0;
}
.lp-hero__title-accent { color: var(--color-accent, #e11d2a); }
.lp-hero__rule {
  display: block;
  width: 68px; height: 4px;
  background: var(--color-accent, #e11d2a);
  border-radius: 2px;
  margin: 26px 0;
}
.lp-hero__lead {
  font-size: clamp(18px, 2vw, 22px);
  line-height: 1.5;
  color: #e6e6e6;
  margin: 0 0 22px;
  max-width: 90%;
}
.lp-hero__body {
  font-size: clamp(15px, 1.5vw, 18px);
  line-height: 1.7;
  color: var(--color-textMuted, #a8a8a8);
  margin: 0 0 32px;
  max-width: 80%;
}
.lp-hero__hl { color: var(--color-accent, #e11d2a); font-weight: 700; }
.lp-hero__cats {
  font-size: clamp(13px, 1.3vw, 15px);
  line-height: 1.7;
  color: var(--color-textDim, #6e6e6e);
  margin: 0 0 32px;
  max-width: 80%;
}

.lp-hero__actions { display: flex; flex-wrap: wrap; gap: 16px; }

/* Buttons */
.lp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background .15s, border-color .15s, transform .1s;
}
.lp-btn:active { transform: translateY(1px); }
.lp-btn--primary {
  background: var(--color-accent, #e11d2a);
  color: #fff;
}
.lp-btn--primary:hover { background: var(--color-accentHover, #f03240); }
.lp-btn--outline {
  background: transparent;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}
.lp-btn--outline:hover { border-color: #fff; background: rgba(255, 255, 255, 0.06); }
.lp-btn--sm { padding: 12px 24px; font-size: 16px; }
.lp-btn--wide { padding: 18px 72px; font-size: 20px; margin: 8px 0 4px; }
.lp-btn__ico { display: inline-flex; }
.lp-btn__ico >>> svg { width: 20px; height: 20px; }

/* ── Feature strip ── */
.lp-features {
  list-style: none;
  margin: 10px auto 0;
  padding: 20px clamp(8px, 2vw, 24px);
  max-width: 1440px;
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 0;
  /* Boxed panel: dark shadow evenly all around (not just one edge), no border. */
  background: rgba(255, 255, 255, 0.025);
  border-radius: 14px;
  box-shadow: 0 0 34px 4px rgba(0, 0, 0, 0.6);
}
.lp-feature {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px clamp(12px, 2vw, 28px);
  font-size: clamp(15px, 1.4vw, 18px);
  font-weight: 500;
  text-align: center;
}
/* Divider line between features. */
.lp-feature:not(:last-child) {
  border-inline-end: 1px solid rgba(255, 255, 255, 0.14);
}
.lp-feature__icon {
  display: inline-flex;
  color: var(--color-accent, #e11d2a);
}
.lp-feature__icon >>> svg { width: 30px; height: 30px; }

/* ══ Shared section scaffolding ══ */
.lp-sec { padding: clamp(48px, 7vw, 96px) clamp(20px, 5vw, 64px); position: relative; }
.lp-sec__inner { max-width: 1280px; margin: 0 auto; }
.lp-eyebrow {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  color: var(--color-accent, #e11d2a);
  font-size: 15px; font-weight: 700; letter-spacing: .2px;
  margin: 0 0 18px;
}
.lp-eyebrow__dash { width: 26px; height: 2px; background: var(--color-accent, #e11d2a); border-radius: 1px; }
.lp-sec__title {
  font-size: clamp(30px, 4.2vw, 52px);
  font-weight: 800; line-height: 1.15; letter-spacing: -0.6px;
  margin: 0;
}
.lp-sec__title--center { text-align: center; }
.lp-red { color: var(--color-accent, #e11d2a); }
.lp-sec__rule {
  display: block; width: 60px; height: 3px; border-radius: 2px;
  background: var(--color-accent, #e11d2a); margin: 22px 0 28px;
}

/* ══ Section 1 — Why join ══ */
.lp-eyebrow--start { justify-content: flex-start; }
.lp-why__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(28px, 4vw, 64px);
  align-items: center;
}
.lp-why__copy { text-align: right; }
.lp-why__body {
  font-size: clamp(15px, 1.5vw, 18px); line-height: 1.8;
  color: var(--color-textMuted, #a8a8a8);
}
.lp-why__body p { margin: 0 0 18px; }

/* Speaker marquee — 3 auto-scrolling rows.
   Modern path (Chrome): each card rides an offset-path line via shape() +
   sibling-index()/sibling-count(), no DOM duplication, any card count,
   responsive (line length derived from container 100%). Technique:
   https://css-tricks.com — "Infinite Marquee Animation using Modern CSS".
   Fallback (Safari/Firefox, no offset-path): a plain static flex row — cards
   stay visible and legible, just not animated. */
.lp-marquee {
  --card-w: 170px;   /* card width */
  --gap: 14px;       /* min gap between cards */
  --dur: 46s;        /* per-card traversal time */
  --visible: 3;      /* cards visible across the row at once */

  display: flex;
  flex-direction: column;
  gap: var(--gap);
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent);
  mask-image: linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent);
}
.lp-marquee__row {
  /* Fallback layout: static horizontal row. */
  display: flex;
  gap: var(--gap);
}
.lp-star {
  position: relative;
  flex: 0 0 auto;
  width: var(--card-w);
  height: 150px;
  border-radius: 12px;
  overflow: hidden;
  /* Explicit longhands so the inline `background-image` (set per-star in the
     template) composes with a guaranteed cover/center fill — no bright sliver
     where a shorthand image layer would otherwise leave size/position unset. */
  background-color: #1a1a1a;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  /* No light border — it rendered as a bright hairline around the photo. */
  border: 1px solid rgba(0, 0, 0, 0.35);
  display: flex; align-items: flex-end;
}
.lp-star--empty { background: linear-gradient(150deg, #2a3348, #12161f); }
/* Scrim over the WHOLE square (inset:0, no transparent top band), deepening
   toward the bottom for name legibility — fixes the uncovered/bright top and
   the bright strip at the image edge. */

.lp-star__name {
  position: relative; z-index: 1; width: 100%;
  padding: 10px 12px;
  font-size: 15px; font-weight: 700; color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,.9);
}

/* Modern marquee — only where offset-path + shape() are supported (Chrome). */
@supports (offset-path: shape(from 0 0, hline by 1px)) {
  .lp-marquee__row {
    display: block;
    position: relative;
    height: 150px;
  }
  .lp-star {
    position: absolute;
    top: 0;
    /* Line runs from just off the start edge to the full width of all cards,
       min = cards side by side (no overlap), else stretches with the container. */
    offset: shape(from calc(var(--card-w) / -2) 50%,
      hline by calc(sibling-count() * max(100% / var(--visible), var(--card-w) + var(--gap))));
    animation: lp-star-slide var(--dur) linear infinite
      calc(-1 * sibling-index() * var(--dur) / sibling-count());
  }
  .lp-marquee__row--rev .lp-star { animation-direction: reverse; }
  .lp-marquee:hover .lp-star { animation-play-state: paused; }
}
@keyframes lp-star-slide {
  to { offset-distance: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-star { animation: none; }
}

/* ══ Section 2 — Cards ══ */
.lp-inside { background: rgba(255,255,255,0.015); }
.lp-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 22px;
  margin-top: clamp(36px, 5vw, 56px);
}
.lp-card {
  background: var(--color-surface1, #0b0b0b);
  border: 1px solid var(--color-border, #2e2e2e);
  border-radius: 16px;
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: transform .18s, border-color .18s;
}
.lp-card:hover { transform: translateY(-6px); border-color: rgba(225,29,42,0.5); }
.lp-card__media {
  position: relative;
  aspect-ratio: 16 / 10;
  background: #161616 center / cover no-repeat;
}
.lp-card__media::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,.85) 100%);
}
.lp-card__icon {
  position: absolute; z-index: 1;
  bottom: -26px; right: 22px;
  width: 52px; height: 52px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--color-surface1, #0b0b0b);
  border: 2px solid var(--color-accent, #e11d2a);
  color: var(--color-accent, #e11d2a);
}
.lp-card__icon >>> svg { width: 26px; height: 26px; }
.lp-card__title {
  margin: 40px 22px 10px;
  font-size: 19px; font-weight: 700; line-height: 1.35;
}
.lp-card__body {
  margin: 0 22px 26px;
  font-size: 15px; line-height: 1.7;
  color: var(--color-textMuted, #a8a8a8);
}

/* ══ Section 3 — Who it's for ══ */
.lp-audience { padding-bottom: 0; text-align: center; }
.lp-audience__body {
  margin: 26px auto 0;
  font-size: clamp(16px, 1.8vw, 20px); line-height: 1.9;
  color: #d0d0d0;
}
.lp-audience__strip {
  display: block;
  width: 100%;
  margin-top: clamp(40px, 6vw, 72px);
  margin-bottom:90px;
  height: clamp(200px, 24vw, 340px);
  object-fit: cover;
  background: #0a0a0a;
  -webkit-mask-image: linear-gradient(180deg, transparent, #000 22%);
  mask-image: linear-gradient(180deg, transparent, #000 22%);
}

/* ══ Section 3.5 — Manifesto ══ */
.lp-manifesto {
  text-align: center;
  background:
    radial-gradient(80% 120% at 50% 0%, rgba(225,29,42,0.10) 0%, transparent 60%),
    rgba(255,255,255,0.015);
  border-top: 1px solid rgba(255,255,255,0.06);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.lp-manifesto__inner { max-width: 900px; }
.lp-manifesto__lead {
  font-size: clamp(15px, 1.6vw, 19px); font-weight: 600;
  color: var(--color-textMuted, #a8a8a8);
  margin: 0 0 20px;
}
.lp-manifesto__title {
  font-size: clamp(28px, 4vw, 48px);
  font-weight: 800; line-height: 1.25; letter-spacing: -0.5px;
  margin: 0 0 22px;
}
.lp-manifesto__sub {
  font-size: clamp(16px, 1.8vw, 21px); line-height: 1.7;
  color: #d6d6d6; margin: 0 0 24px;
}
.lp-manifesto__cta {
  font-size: clamp(15px, 1.5vw, 18px); font-weight: 600;
  color: #fff; margin: 0;
}

/* ══ Section 4 — Final CTA ══ */
.lp-cta { overflow: hidden; text-align: center; }
.lp-cta__bg {
  position: absolute; inset: 0; z-index: 0;
  background: #050505;
}
.lp-cta__bg::after {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(60% 90% at 50% 50%, rgba(5,5,5,0.96) 40%, rgba(5,5,5,0.72) 100%);
}
.lp-cta__inner {
  position: relative; z-index: 1;
  max-width: 760px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: center;
}
.lp-cta__diamond {
  width: 12px; height: 12px; margin-bottom: 22px;
  background: var(--color-accent, #e11d2a);
  transform: rotate(45deg);
}
.lp-cta__title {
  font-size: clamp(34px, 5.5vw, 66px);
  font-weight: 800; line-height: 1.12; letter-spacing: -0.8px;
  margin: 0 0 22px;
}
.lp-cta__body {
  max-width: 52ch;
  font-size: clamp(15px, 1.6vw, 19px); line-height: 1.7;
  color: var(--color-textMuted, #c2c2c2);
  margin: 0 0 30px;
}
.lp-features--cta { border-top: none; margin-top: 30px; padding-bottom: 0; }

/* ── Sticky CTA ── */
.lp-sticky {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(12px, 3vw, 32px);
  flex-wrap: wrap;
  padding: 14px clamp(16px, 4vw, 48px) calc(14px + env(safe-area-inset-bottom));
  background: rgba(11, 11, 11, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.5);
}
.lp-sticky__text {
  font-size: clamp(14px, 1.4vw, 17px);
  font-weight: 600;
  color: #ececec;
}
.lp-sticky-enter-active, .lp-sticky-leave-active { transition: transform .25s ease, opacity .25s ease; }
.lp-sticky-enter, .lp-sticky-leave-to { transform: translateY(100%); opacity: 0; }

/* ── Responsive ── */
/* Below the 2-column split: collage becomes a dimmed top-anchored backdrop,
   copy stacks full-width over it. */
@media (max-width: 900px) {
  .lp-topbar {
    padding-inline: 16px;
  }
  .lp-hero {
    padding: 24px 16px 0;
  }
  .lp-hero__inner { direction: rtl; min-height: 0; }
  .lp-hero__copy { max-width: none; }
  .lp-hero__title {
    font-size: clamp(34px, 9vw, 52px);
    line-height: 1.1;
  }
  .lp-hero__rule { margin: 18px 0; }
  .lp-hero__cats { max-width: none; margin-bottom: 24px; }
  .lp-hero__actions {
    gap: 10px;
  }
  .lp-btn {
    min-height: 52px;
  }
  .lp-hero__collage {
    width: 130%;
    right: -15%;
    opacity: 0.28;
  }
  .lp-hero__lead, .lp-hero__body { max-width: none; }
  .lp-features {
    margin-top: 18px;
    padding: 10px;
    border-radius: 12px;
  }
  .lp-feature {
    font-size: 16px;
    padding: 12px;
  }
  .lp-sec {
    padding: 56px 16px;
  }
  .lp-manifesto__title {
    font-size: clamp(26px, 7vw, 40px);
  }
  .lp-audience__strip {
    margin-top: 34px;
    margin-bottom: 72px;
    height: clamp(170px, 42vw, 250px);
  }
  .lp-cards { grid-template-columns: repeat(2, 1fr); }
  .lp-why__grid { grid-template-columns: 1fr; }
}
@media (max-width: 560px) {
  .lp-topbar__brand {
    gap: 10px;
  }
  .lp-topbar__logo {
    width: 62px;
    height: 34px;
  }
  .lp-topbar__name {
    font-size: 17px;
  }
  .lp-topbar__sub {
    font-size: 11px;
  }
  .lp-hero {
    padding-top: 18px;
  }
  .lp-hero__title {
    font-size: clamp(30px, 10vw, 40px);
  }
  .lp-hero__lead {
    font-size: 17px;
    margin-bottom: 16px;
  }
  .lp-hero__body {
    font-size: 15px;
    margin-bottom: 20px;
  }
  .lp-hero__cats {
    font-size: 13px;
  }
  .lp-btn { flex: 1; }
  .lp-btn--wide { width: 100%; padding-left: 24px; padding-right: 24px; }
  .lp-btn--outline,
  .lp-btn--primary {
    width: 100%;
  }
  .lp-hero__actions {
    flex-direction: column;
  }
  .lp-cards { grid-template-columns: 1fr; }
  .lp-card__title {
    margin: 34px 16px 8px;
    font-size: 18px;
  }
  .lp-card__body {
    margin: 0 16px 20px;
    font-size: 14px;
  }
  .lp-star { width: 144px; height: 128px; }
  .lp-sticky__text { display: none; }
  .lp-sticky {
    justify-content: stretch;
    padding-inline: 12px;
  }
  .lp-sticky .lp-btn { flex: 1; }
  /* Stack features vertically with horizontal dividers between them. */
  .lp-features { flex-direction: column; }
  .lp-feature { width: 100%; padding: 12px; }
  .lp-feature:not(:last-child) {
    border-inline-end: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.14);
  }
  .lp-audience__strip {
    margin-bottom: 58px;
  }
}
</style>
